import React, { useState } from 'react';

import { AiContentFeedbackScore } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentFeedbackScoreEnum';

import AiContentEventFactory from '../../../../../../Events/AiContentEventFactory';
import events from '../../../../../../Events/Events';
import { Button } from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';

type Reaction = {
  id: string;
  alt: string;
  defaultIcon: string;
  selectedIcon: string;
  aiContentFeedbackScore: AiContentFeedbackScore;
};

const reactions: Reaction[] = [
  {
    id: 'happy',
    alt: 'Satisfied with Video Edit',
    defaultIcon: `${__ASSETS_COMMON_IMAGES_URL__}/icons/feedback/happy.svg`,
    selectedIcon: `${__ASSETS_COMMON_IMAGES_URL__}/icons/feedback/happy-selected.svg`,
    aiContentFeedbackScore: AiContentFeedbackScore.HAPPY_FACE,
  },
  {
    id: 'neutral',
    alt: 'Neutral about Video Edit',
    defaultIcon: `${__ASSETS_COMMON_IMAGES_URL__}/icons/feedback/neutral.svg`,
    selectedIcon: `${__ASSETS_COMMON_IMAGES_URL__}/icons/feedback/neutral-selected.svg`,
    aiContentFeedbackScore: AiContentFeedbackScore.NEUTRAL_FACE,
  },
  {
    id: 'sad',
    alt: 'Dissatisfied with Video Edit',
    defaultIcon: `${__ASSETS_COMMON_IMAGES_URL__}/icons/feedback/sad.svg`,
    selectedIcon: `${__ASSETS_COMMON_IMAGES_URL__}/icons/feedback/sad-selected.svg`,
    aiContentFeedbackScore: AiContentFeedbackScore.SAD_FACE,
  },
];

interface VideoEditFeedbackProps {
  handleClose: () => void;
  contentId: number | null;
}

export default function VideoEditFeedback({
  handleClose,
  contentId,
}: VideoEditFeedbackProps) {
  const [completedFeedback, setCompletedFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [selectedReaction, setSelectedReaction] = useState<Reaction | null>(
    null
  );

  if (completedFeedback) {
    return (
      <div className="flex flex-col items-center">
        <Text fontSize="base" fontWeight="regular">
          Thanks! Your feedback is valuable to us and helps us improve
        </Text>
        <Button variant="outlined-light" onClick={handleClose}>
          Back to Search
        </Button>
      </div>
    );
  }

  return (
    <>
      <Text fontSize="base" fontWeight="regular">
        Did this video meet your expectations?
      </Text>

      <div className="flex gap-4 mb-4">
        {reactions.map((reaction) => (
          <button
            key={reaction.id}
            onClick={() => setSelectedReaction(reaction)}
          >
            <img
              src={
                selectedReaction?.id === reaction.id
                  ? reaction.selectedIcon
                  : reaction.defaultIcon
              }
              alt={reaction.alt}
              className="w-12 h-12"
            />
          </button>
        ))}
      </div>
      {selectedReaction && (
        <div className="w-full bg-gray-900 border-2 border-gray-700 rounded-3xl text-white mb-6 h-[100px]">
          <div className="p-4">
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Let us know what you thought to help us improve your experience"
              className="w-full bg-transparent border-0 resize-none outline-none placeholder-white text-sm h-24"
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="outlined-light"
                onClick={() => {
                  const aiContentFeedbackEvent =
                    AiContentEventFactory.createFeedbackEvent(
                      feedback,
                      selectedReaction.aiContentFeedbackScore,
                      contentId
                    );
                  events.produce(aiContentFeedbackEvent);
                  setCompletedFeedback(true);
                }}
              >
                Submit Feedback
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
